/**
 * Client-side heuristic documentation quality scorer.
 *
 * Approximates the server-side LLM-based documentation-quality-eval using
 * keyword/pattern matching. Used on Oreja task proposal cards to show
 * estimated doc quality scores during review.
 *
 * @module doc-quality-scorer
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Result of scoring a task's documentation quality. */
export interface DocQualityScore {
  /** Overall score from 0-100. */
  score: number;
  /** Letter grade: A (>= 90), B (>= 75), C (>= 60), D (>= 40), F (< 40). */
  grade: string;
  /** Description quality sub-score (0-40). */
  description_score: number;
  /** Comment activity sub-score (0-30). */
  comment_score: number;
  /** Current state clarity sub-score (0-30). */
  clarity_score: number;
}

// ---------------------------------------------------------------------------
// Pattern banks
// ---------------------------------------------------------------------------

const ACCEPTANCE_PATTERNS: RegExp[] = [
  /acceptance criteria/i,
  /done when/i,
  /definition of done/i,
  /expected (result|outcome|behavior)/i,
  /should (be able to|result in)/i,
  /success (criteria|looks like)/i,
];

const BLOCKER_PATTERNS: RegExp[] = [
  /block(ed|er|ing)/i,
  /depend(s|ency|encies)/i,
  /waiting (on|for)/i,
  /prerequisite/i,
  /requires/i,
];

const STATE_PATTERNS: RegExp[] = [
  /current (state|status|progress)/i,
  /so far/i,
  /completed|done|finished/i,
  /remaining|left to do/i,
  /in progress|working on/i,
  /next step/i,
];

const DECISION_PATTERNS: RegExp[] = [
  /decided|decision/i,
  /chose|chosen/i,
  /approach/i,
  /because|rationale|reason/i,
  /trade-?off/i,
];

const EXTERNAL_LINK_PATTERNS: RegExp[] = [
  /https?:\/\/(docs\.google|notion\.so|loom\.com|figma\.com|miro\.com|confluence)/i,
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Count how many patterns from a bank match in the given text.
 *
 * @param text - Text to search in.
 * @param patterns - Array of RegExp patterns to test.
 * @returns Number of matching patterns.
 */
function countMatches(text: string, patterns: RegExp[]): number {
  return patterns.filter((p) => p.test(text)).length;
}

/**
 * Count words in a string (splits on whitespace).
 *
 * @param text - Input text.
 * @returns Word count.
 */
function wordCount(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

/**
 * Clamp a number between min and max (inclusive).
 *
 * @param value - Value to clamp.
 * @param min - Minimum bound.
 * @param max - Maximum bound.
 * @returns Clamped value.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// ---------------------------------------------------------------------------
// Sub-scorers
// ---------------------------------------------------------------------------

/**
 * Score the description quality (0-40 points).
 *
 * Rubric:
 * - 0: No description or just repeats the title (< 5 words)
 * - 10: Very brief, missing context (5-15 words)
 * - 20: Basic description but lacks acceptance criteria (16-40 words)
 * - 30: Good description with context and some specifics (41+ words or patterns)
 * - 40: Excellent - clear objective, acceptance criteria, relevant context
 * - Bonus: external doc links (+3 each, up to +6)
 *
 * @param description - Task description text.
 * @returns Score from 0 to 40.
 */
function scoreDescriptionQuality(description: string): number {
  const words = wordCount(description);

  if (words === 0) return 0;

  // Base score from length
  let score: number;
  if (words < 5) {
    score = 5;
  } else if (words < 16) {
    score = 10;
  } else if (words <= 40) {
    score = 20;
  } else {
    score = 25; // Good length baseline
  }

  // Pattern bonuses
  const acceptanceHits = countMatches(description, ACCEPTANCE_PATTERNS);
  const blockerHits = countMatches(description, BLOCKER_PATTERNS);
  const decisionHits = countMatches(description, DECISION_PATTERNS);

  // Each acceptance pattern match adds up to +5 (max +10)
  score += Math.min(acceptanceHits * 5, 10);

  // Blocker/decision context adds minor bonus
  if (blockerHits > 0) score += 2;
  if (decisionHits > 0) score += 2;

  // External link bonus (+3 each, up to +6)
  const linkHits = countMatches(description, EXTERNAL_LINK_PATTERNS);
  score += Math.min(linkHits * 3, 6);

  return clamp(score, 0, 40);
}

/**
 * Score the comment activity (0-30 points).
 *
 * Rubric:
 * - 0: No comments at all
 * - 10: Only status updates with no detail
 * - 20: Some substantive comments but gaps
 * - 30: Regular, detailed comments documenting progress, decisions, blockers
 *
 * @param comments - Concatenated comment text.
 * @returns Score from 0 to 30.
 */
function scoreCommentActivity(comments: string): number {
  const words = wordCount(comments);

  if (words === 0) return 0;
  if (words < 10) return 5;

  // Base score from length
  let score: number;
  if (words < 30) {
    score = 10;
  } else if (words < 60) {
    score = 15;
  } else {
    score = 20;
  }

  // Substantive content bonuses
  const decisionHits = countMatches(comments, DECISION_PATTERNS);
  const blockerHits = countMatches(comments, BLOCKER_PATTERNS);
  const stateHits = countMatches(comments, STATE_PATTERNS);

  if (decisionHits > 0) score += 3;
  if (blockerHits > 0) score += 3;
  if (stateHits > 0) score += 3;

  return clamp(score, 0, 30);
}

/**
 * Score the current state clarity (0-30 points).
 *
 * Evaluates both description and comments for clarity about current state.
 *
 * Rubric:
 * - 0: No way to know current status
 * - 10: Status is set but comments don't explain where things stand
 * - 20: Can infer current state from recent comments
 * - 30: Clear documentation of current state, what's done, what's remaining
 *
 * @param description - Task description text.
 * @param comments - Concatenated comment text.
 * @returns Score from 0 to 30.
 */
function scoreStateClarity(description: string, comments: string): number {
  const combined = `${description} ${comments}`;
  const stateHits = countMatches(combined, STATE_PATTERNS);

  if (stateHits === 0) return 0;
  if (stateHits === 1) return 10;
  if (stateHits === 2) return 15;
  if (stateHits === 3) return 20;
  if (stateHits === 4) return 25;
  return 30;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Determine the letter grade for a numeric score.
 *
 * @param score - Numeric score (0-100).
 * @returns Letter grade: A, B, C, D, or F.
 */
export function getGrade(score: number): string {
  if (score >= 90) return 'A';
  if (score >= 75) return 'B';
  if (score >= 60) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

/**
 * Score a task's documentation quality using heuristic pattern matching.
 *
 * Produces a {@link DocQualityScore} with sub-scores for description quality
 * (0-40), comment activity (0-30), and current state clarity (0-30).
 *
 * @param description - The task description text.
 * @param comments - Concatenated comment text (may be empty).
 * @returns Documentation quality score with grade and sub-scores.
 */
export function scoreDescription(
  description: string,
  comments: string,
): DocQualityScore {
  const description_score = scoreDescriptionQuality(description);
  const comment_score = scoreCommentActivity(comments);
  const clarity_score = scoreStateClarity(description, comments);

  const score = clamp(description_score + comment_score + clarity_score, 0, 100);
  const grade = getGrade(score);

  return {
    score,
    grade,
    description_score,
    comment_score,
    clarity_score,
  };
}
